import { useEffect, useState } from 'react';
import { bt, btp } from '../i18n';
import { useBuilderStore, type SaveState } from '../state/builderStore';

/**
 * The topbar save-state indicator (build-plan §7.2 / §7.3, D17) — a custom in-app, token-driven status
 * line that REPLACES the mockup's inert "Save draft" ghost button. NEVER a native popup.
 *
 * It surfaces the store's `saveState`:
 *   idle/saved      → "Saved · <relative time>" (or "All changes saved")
 *   unsaved/dirty   → "Unsaved changes"
 *   saving          → "Saving…" (spinner; disabled under prefers-reduced-motion via CSS)
 *
 * `saving` and `unsaved` are DELAY-GATED (see useDelayedTransientState) so a normal fast autosave never
 * flashes them — the topbar must not twitch on every keystroke.
 *   offline         → "Reconnecting…" (assertive — the seller should know saves are paused)
 *   error           → "Couldn't save · Retry"
 *   conflict        → "Edited elsewhere" (the reconcile modal carries the actual choice)
 *
 * `aria-live="polite"` for the calm states, but error/offline read assertively so a screen-reader user
 * isn't left thinking their work is saved when it isn't.
 */
/**
 * How long a transient state must PERSIST before it's allowed on screen. `saving` gets a shorter fuse
 * than `unsaved` because a save that outlives it really is slow enough to report, whereas `unsaved` is
 * expected for the whole 800ms autosave debounce and only means something once that window has passed.
 */
const BUSY_DELAY_MS = 700;
const UNSAVED_DELAY_MS = 2_000;

/** States that must never be delayed — the seller has to know immediately that saving stopped working. */
function isUrgent(s: SaveState): boolean {
  return s === 'error' || s === 'offline' || s === 'conflict';
}

/**
 * Returns `raw` immediately for calm + urgent states, but holds back `saving` / `unsaved` / `dirty`
 * until they've lasted past their threshold. If the state settles first (the common case: a fast save),
 * the transient value is never rendered at all, so the topbar stays visually still while typing.
 */
function useDelayedTransientState(raw: SaveState): SaveState {
  const [shown, setShown] = useState(raw);

  useEffect(() => {
    if (isUrgent(raw) || (raw !== 'saving' && raw !== 'unsaved' && raw !== 'dirty')) {
      setShown(raw);
      return;
    }
    const delay = raw === 'saving' ? BUSY_DELAY_MS : UNSAVED_DELAY_MS;
    const id = setTimeout(() => setShown(raw), delay);
    return () => clearTimeout(id);
  }, [raw]);

  return shown;
}

function relativeTime(ts: number, now: number): string {
  const secs = Math.max(0, Math.round((now - ts) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return btp('chrome.save.secs_ago', '{n}s ago', { n: secs });
  const mins = Math.round(secs / 60);
  if (mins < 60) return btp('chrome.save.mins_ago', '{n}m ago', { n: mins });
  const hrs = Math.round(mins / 60);
  return btp('chrome.save.hours_ago', '{n}h ago', { n: hrs });
}

export function SaveStateIndicator() {
  const rawState = useBuilderStore((s) => s.saveState);
  const lastSavedAt = useBuilderStore((s) => s.lastSavedAt);

  // QUIET AUTOSAVE (founder, 2026-08-01): a normal save round-trips in ~200ms, so rendering the
  // `unsaved → saving → saved` cascade verbatim flashes three labels + a spinner on EVERY keystroke.
  // That's motion the seller must ignore, next to the canvas they're actually watching. So the two
  // transient states are DELAY-GATED: they only reach the DOM if the transition is slow enough to be
  // worth reporting. Below the threshold the indicator simply holds the calm "Saved · X" it already
  // showed — which stays true, since that save did happen. Failure states (error/offline/conflict) are
  // NEVER gated: those must surface the instant they happen.
  const saveState = useDelayedTransientState(rawState);

  // Tick once a minute so the "Xm ago" label stays roughly fresh without a heavy interval.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (saveState !== 'saved' || lastSavedAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [saveState, lastSavedAt]);

  let label: string;
  let tone: 'muted' | 'busy' | 'warn' | 'error' = 'muted';
  let assertive = false;

  switch (saveState) {
    case 'saving':
      label = bt('chrome.common.saving', 'Saving…');
      tone = 'busy';
      break;
    case 'saved':
      label = lastSavedAt
        ? btp('chrome.save.saved_ago', 'Saved · {when}', { when: relativeTime(lastSavedAt, now) })
        : bt('chrome.save.all_saved', 'All changes saved');
      tone = 'muted';
      break;
    case 'unsaved':
    case 'dirty':
      label = bt('chrome.save.unsaved', 'Unsaved changes');
      tone = 'warn';
      break;
    case 'offline':
      label = bt('chrome.save.offline', 'Offline · retrying…');
      tone = 'warn';
      assertive = true;
      break;
    case 'error':
      label = bt('chrome.save.failed', 'Couldn’t save');
      tone = 'error';
      assertive = true;
      break;
    case 'conflict':
      label = bt('chrome.save.conflict', 'Edited elsewhere');
      tone = 'error';
      assertive = true;
      break;
    case 'idle':
    default:
      label = lastSavedAt
        ? bt('chrome.save.all_saved', 'All changes saved')
        : bt('chrome.save.not_yet', 'Not saved yet');
      tone = 'muted';
      break;
  }

  return (
    <span
      className={`bld-save-state bld-save-state--${tone}`}
      role="status"
      aria-live={assertive ? 'assertive' : 'polite'}
      data-state={saveState}
    >
      {tone === 'busy' && <span className="bld-save-state__spinner" aria-hidden="true" />}
      {label}
    </span>
  );
}
